import { classifyFoundryError } from "@m8t-stack/foundry-invoke";
import { LocalCliError } from "./errors.js";

export interface ProbeResult { ok: boolean; status?: number; message?: string }

/**
 * Probe the Foundry data plane until it serves the project on `consecutive` successive
 * tries (one lucky 200 isn't "ready" on an eventually-consistent fresh account), or give
 * up after `attempts`. A retryable probe failure (transient 404 "Project not found", 5xx,
 * rate-limit) resets the streak and keeps going; a NON-retryable failure (auth) throws.
 *
 * `retryableStatuses` forces the listed HTTP statuses to be treated as retryable regardless
 * of the classifier verdict. Used by the per-agent gate so a registration-propagation 404
 * ("Agent not found") keeps polling instead of failing fast.
 */
export async function awaitDataPlaneReady(opts: {
  probe: () => Promise<ProbeResult>;
  consecutive?: number;
  attempts?: number;
  intervalMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onProgress?: (m: string) => void;
  retryableStatuses?: number[];
}): Promise<{ ready: boolean; attempts: number }> {
  const consecutive = opts.consecutive ?? 3;
  const attempts = opts.attempts ?? 60;
  const intervalMs = opts.intervalMs ?? 5_000;
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  let streak = 0;
  for (let i = 1; i <= attempts; i++) {
    const r = await opts.probe();
    if (r.ok) {
      streak++;
      opts.onProgress?.(`data-plane ok (${streak.toString()}/${consecutive.toString()})`);
      if (streak >= consecutive) return { ready: true, attempts: i };
    } else {
      const cls = classifyFoundryError({ status: r.status, message: r.message ?? "" });
      const forcedRetryable = r.status !== undefined && (opts.retryableStatuses?.includes(r.status) ?? false);
      if (!forcedRetryable && !cls.retryable) {
        throw new LocalCliError({
          code: "FOUNDRY_NOT_READY_FATAL",
          message: `Foundry data plane returned a non-retryable error: ${cls.message}`,
          hint: "This is not a transient data-plane delay — check `az login`/permissions and the endpoint.",
          retryable: false,
        });
      }
      streak = 0;
      opts.onProgress?.(`data-plane not ready (${forcedRetryable ? `status ${(r.status ?? 0).toString()}` : cls.category}), waiting…`);
    }
    if (i < attempts) await sleep(intervalMs);
  }
  return { ready: false, attempts };
}
